"use client";

import { useState } from "react";
import { ArrowUpRight, Plus } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const manifestoItems = [{
  question: "What materials are compatible with your laser marking?",
  answer: "We specialize in high-precision marking on stainless steel, anodized aluminum, brass, and industrial-grade plastics. Our laser systems are calibrated to ensure permanent, high-contrast marks that withstand harsh environmental conditions.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/oem_material_compatibility.png",
  imageAlt: "Laser marking on industrial metal surface"
}, {
  question: "How do you ensure compliance with UL 508A standards?",
  answer: "Our marking processes are fully compliant with UL 508A requirements for industrial control panels. We maintain strict traceability and use certified materials to ensure your labels meet all regulatory and safety specifications.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/oem_ul_compliance.png",
  imageAlt: "UL certified label on control panel"
}, {
  question: "What is your typical turnaround time for custom orders?",
  answer: "Standard production cycles are completed within 5 to 7 business days. For urgent submittal packages or critical infrastructure projects, we offer expedited processing to ensure your project timeline remains on track.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/oem_production_timeline.png",
  imageAlt: "Organized production floor with finished parts"
}, {
  question: "Can you handle direct part marking (DPM) for traceability?",
  answer: "Yes, we provide high-resolution DPM services including UID, QR codes, and serial numbers. Our systems ensure deep, permanent marking that remains readable even after exposure to extreme heat, chemicals, or abrasion.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/oem_dpm_traceability.png",
  imageAlt: "Close up of laser etched serial number"
}, {
  question: "Do you offer design support for custom labels?",
  answer: "Our engineering team assists with layout optimization, material selection, and file conversion. We ensure your designs are optimized for laser engraving to achieve maximum clarity and durability for your specific application.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/oem_design_support.png",
  imageAlt: "Engineer reviewing label design on screen"
}, {
  question: "How do you handle large-scale industrial projects?",
  answer: "We utilize automated fiber laser systems capable of high-volume production runs. Whether you need thousands of identical tags or complex custom assemblies, our facility is equipped to scale with your project requirements.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/oem_industrial_scale.webp",
  imageAlt: "Automated laser marking station in operation"
}, {
  question: "What quality control measures are in place?",
  answer: "Every batch undergoes a multi-point inspection, including visual verification and material testing. We provide detailed quality reports and certificates of conformance with every shipment to guarantee absolute precision.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/oem_quality_control.png",
  imageAlt: "Quality inspector checking laser mark depth"
}, {
  question: "How do I submit files for custom engraving?",
  answer: "We accept vector files including DXF, AI, and SVG formats. Simply upload your files through our secure client portal, and our team will review them for manufacturability before beginning the production process.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/oem_file_submission.png",
  imageAlt: "Digital file upload interface on monitor"
}];
export default function () {
  const [openValue, setOpenValue] = useState<string>("item-0");
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="1664" id="oemmarkingfaq" className="relative bg-background text-foreground py-24 md:py-32 overflow-hidden">
      <div className="max-w-[1440px] mx-auto px-6 md:px-16 lg:px-22">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div className="lg:col-span-5 relative">
            <div ref={headerRef} className={`lg:sticky lg:top-28 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
              {}
              <div aria-hidden="true" className="pointer-events-none absolute -top-10 -left-4 lg:-left-8 select-none font-serif font-light leading-none text-foreground/5" style={{
              fontSize: "20vw"
            }}>
                05
              </div>

              {}
              <div className="relative flex items-center gap-3 mb-8">
                <span className="font-mono text-xs uppercase tracking-[0.18em] text-primary">
                  § 05 / FAQ — MARKING SOLUTIONS
                </span>
              </div>

              {}
              <div className="relative flex items-center mb-10">
                <div className="h-px w-20 bg-primary" />
                <div className="h-1 w-1 bg-primary" />
              </div>

              {}
              <h2 className="relative font-default font-light leading-[1.0] tracking-tight text-foreground mb-8" style={{
              fontSize: "clamp(2.25rem, 5.4vw, 4.75rem)"
            }}>
                Marking <span className="italic text-primary">Expertise</span>
              </h2>

              {}
              <p className="relative font-default text-foreground/85 leading-relaxed mb-10 max-w-md" style={{
              fontSize: "clamp(1.0625rem, 1.2vw, 1.25rem)"
            }}>
                Common questions regarding our industrial laser marking and engraving services.
              </p>

              {}
              <Link to="/ContactPage" className="relative inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.16em] text-primary group">
                <span className="relative">
                  Contact Support
                  <span className="absolute left-0 -bottom-1 h-px bg-primary transition-all duration-300 w-[60%] group-hover:w-full" />
                </span>
                <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
          </div>

          {}
          <div className="lg:col-span-7">
            <Accordion type="single" collapsible value={openValue} onValueChange={v => setOpenValue(v)} className="w-full">
              {manifestoItems.map((item, i) => {
              const itemValue = `item-${i}`;
              const isOpen = openValue === itemValue;
              const indexLabel = `${String(i + 1).padStart(2, "0")} / ${String(manifestoItems.length).padStart(2, "0")}`;
              return <AccordionItem key={i} data-index={i} value={itemValue} className="relative border-b-0 border-t border-foreground/20">
                    {}
                    <span aria-hidden="true" className={`pointer-events-none absolute left-0 top-0 w-[2px] bg-primary transition-all duration-300 ease-out ${isOpen ? "h-full opacity-100" : "h-0 opacity-0"}`} />

                    <AccordionTrigger className={`group flex items-center justify-between gap-6 py-6 hover:no-underline rounded-md transition-colors duration-300 [&>svg]:hidden ${isOpen ? "text-foreground" : "text-foreground/85"}`}>
                      <span className="flex-1 text-left font-default font-normal leading-tight tracking-tight" style={{
                    fontSize: "clamp(1.125rem, 1.8vw, 1.5rem)"
                  }}>
                        {item.question}
                      </span>
                      <span className="hidden sm:block font-mono text-xs uppercase tracking-[0.18em] text-foreground/55 shrink-0">
                        {indexLabel}
                      </span>
                      <span className={`shrink-0 inline-flex items-center justify-center w-7 h-7 text-primary transition-all duration-300 ease-out ${isOpen ? "rotate-45 opacity-100" : "rotate-0 opacity-70 group-hover:opacity-100"}`} aria-hidden="true">
                        <Plus className="h-5 w-5" strokeWidth={1.25} />
                      </span>
                    </AccordionTrigger>

                    <AccordionContent className="pb-8 pt-2 pl-0 md:pl-6">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
                        <p className="md:col-span-2 font-default text-foreground/85 leading-relaxed" style={{
                      fontSize: "0.9375rem",
                      lineHeight: 1.7
                    }}>
                          {item.answer}
                        </p>

                        {}
                        <div className="hidden md:block relative">
                          <div className="relative border border-foreground/15 aspect-[4/5] overflow-hidden">
                            {}
                            <span aria-hidden="true" className="absolute top-2 left-2 w-3 h-3 pointer-events-none">
                              <span className="absolute top-1/2 left-0 w-full h-px bg-primary" />
                              <span className="absolute left-1/2 top-0 h-full w-px bg-primary" />
                            </span>
                            <span aria-hidden="true" className="absolute top-2 right-2 w-3 h-3 pointer-events-none">
                              <span className="absolute top-1/2 left-0 w-full h-px bg-primary" />
                              <span className="absolute left-1/2 top-0 h-full w-px bg-primary" />
                            </span>
                            <span aria-hidden="true" className="absolute bottom-2 left-2 w-3 h-3 pointer-events-none">
                              <span className="absolute top-1/2 left-0 w-full h-px bg-primary" />
                              <span className="absolute left-1/2 top-0 h-full w-px bg-primary" />
                            </span>
                            <span aria-hidden="true" className="absolute bottom-2 right-2 w-3 h-3 pointer-events-none">
                              <span className="absolute top-1/2 left-0 w-full h-px bg-primary" />
                              <span className="absolute left-1/2 top-0 h-full w-px bg-primary" />
                            </span>

                            <img src={item.image} alt={item.imageAlt} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-700 ease-out hover:scale-[1.03]" />
                          </div>
                          <div className="mt-3 font-mono text-[0.7rem] uppercase tracking-[0.18em] text-foreground/55">
                            FIG. {String(i + 1).padStart(2, "0")} — DETAIL
                          </div>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>;
            })}

              {}
              <div className="relative border-t border-foreground/20 pt-6 flex items-center gap-2">
                <div className="h-1 w-1 bg-primary" />
                <div className="h-px w-16 bg-primary" />
                <span className="font-mono text-[0.7rem] uppercase tracking-[0.18em] text-foreground/55 ml-2">
                  End of FAQ
                </span>
              </div>
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}